<template>
  <div class="booking-page">
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      bottom
      right
      app
      class="global-snackbar"
      style="z-index: 100000 !important"
      :z-index="100000"
    >
      {{ snackbar.message }}

      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <v-container fluid class="pa-4 pa-md-6">
      <!-- Page Header -->
      <div class="page-header mb-4">
        <div class="page-header-row">
          <v-btn icon @click="$router.go(-1)" class="back-btn">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <div>
            <h1 class="page-title">Schedule Your Service</h1>
            <p class="service-name">{{ serviceName }}</p>
          </div>
        </div>
      </div>

      <v-row>
        <!-- Left Section - Booking Details -->
        <v-col cols="12" md="8">
          <v-card class="booking-card elevation-1">
            <!-- Step 1: Time & Date Selection -->
            <div class="booking-section">
              <div
                class="step-header"
                :class="{ 'step-completed': isStep1Completed }"
              >
                <div class="step-number">
                  <v-icon v-if="isStep1Completed" color="white" small
                    >mdi-check</v-icon
                  >
                  <span v-else>1</span>
                </div>
                <div class="step-content">
                  <h2 class="step-title">Select Date & Time</h2>
                  <p class="step-description">
                    Choose when you'd like the service
                  </p>
                </div>
              </div>

              <div class="datetime-compact">
                <!-- Date Selection -->
                <div class="compact-group">
                  <label class="compact-label">Date</label>
                  <div class="compact-buttons">
                    <v-btn
                      small
                      :outlined="selectedDate !== 'Today'"
                      :color="selectedDate === 'Today' ? '#f46523' : 'grey'"
                      :dark="selectedDate === 'Today'"
                      :class="{ 'active-compact': selectedDate === 'Today' }"
                      class="compact-btn"
                      @click="selectedDate = 'Today'"
                    >
                      Today
                    </v-btn>
                    <v-btn
                      small
                      :outlined="selectedDate !== 'Tomorrow'"
                      :color="selectedDate === 'Tomorrow' ? '#f46523' : 'grey'"
                      :dark="selectedDate === 'Tomorrow'"
                      :class="{ 'active-compact': selectedDate === 'Tomorrow' }"
                      class="compact-btn"
                      @click="selectedDate = 'Tomorrow'"
                    >
                      Tomorrow
                    </v-btn>
                    <v-menu offset-y>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          small
                          outlined
                          color="#f46523"
                          v-bind="attrs"
                          v-on="on"
                          class="compact-btn"
                        >
                          <v-icon x-small class="mr-1">mdi-calendar</v-icon>
                          {{ customDate || "Other" }}
                        </v-btn>
                      </template>
                      <v-date-picker
                        v-model="customDate"
                        color="#f46523"
                        :min="new Date().toISOString().substr(0, 10)"
                      />
                    </v-menu>
                  </div>
                </div>

                <!-- Time Selection -->
                <div class="compact-group">
                  <label class="compact-label">Time</label>

                  <!-- Button to open sheet -->
                  <v-btn
                    block
                    outlined
                    color="#f46523"
                    class="time-select-btn"
                    @click="timeSheet = true"
                  >
                    <v-icon left size="20">mdi-clock-outline</v-icon>
                    {{ selectedTime || "Select Time" }}
                    <v-spacer></v-spacer>
                    <v-icon right>mdi-chevron-down</v-icon>
                  </v-btn>

                  <!-- Time Selection Dialog -->
                  <v-dialog v-model="timeSheet" max-width="400">
                    <v-card class="time-sheet-card">
                      <v-card-title class="sheet-header">
                        <span class="sheet-title">Select Time</span>
                        <v-spacer></v-spacer>
                        <v-btn icon @click="timeSheet = false">
                          <v-icon>mdi-close</v-icon>
                        </v-btn>
                      </v-card-title>
                      <v-card-text class="sheet-content">
                        <div class="time-list-grid">
                          <div
                            v-for="time in generatedTimeSlots"
                            :key="time"
                            class="time-slot-btn"
                            :class="{
                              selected: selectedTime === time,
                              disabled: isTimeDisabled(time),
                            }"
                            @click="selectTime(time)"
                          >
                            {{ time }}
                          </div>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-dialog>
                </div>
              </div>

              <!-- Selection Summary for Date & Time -->
              <div
                v-if="selectedDate && selectedTime"
                class="selection-summary-pill mt-4"
              >
                <div class="summary-icon">
                  <v-icon color="white" x-small>mdi-calendar-check</v-icon>
                </div>
                <span class="summary-text">
                  Scheduled for
                  <strong>{{
                    selectedDate === "Today"
                      ? "Today"
                      : selectedDate === "Tomorrow"
                      ? "Tomorrow"
                      : customDate
                  }}</strong>
                  at <strong>{{ selectedTime }}</strong>
                </span>
              </div>
            </div>

            <v-divider class="section-divider" />

            <!-- Step 2: Service Partner Selection -->
            <div class="booking-section">
              <div
                class="step-header"
                :class="{
                  'step-completed': isStep2Completed,
                  'step-disabled': !isStep1Completed,
                }"
              >
                <div class="step-number">
                  <v-icon v-if="isStep2Completed" color="white" small
                    >mdi-check</v-icon
                  >
                  <span v-else>2</span>
                </div>
                <div class="step-content">
                  <h2 class="step-title">Choose Service Partner</h2>
                  <p class="step-description">
                    Select your preferred professional
                    <span class="step-optional"
                      >Partner selection optional</span
                    >
                  </p>
                </div>
              </div>

              <div
                class="partner-selected"
                v-if="servicePartner && !partnerFormExpanded"
              >
                <div class="partner-info">
                  <v-avatar size="48" color="#f5f5f5" class="mr-3">
                    <v-img
                      v-if="servicePartner.profile_picture"
                      :src="servicePartner.profile_picture"
                      alt="Partner"
                    />
                    <v-icon v-else color="#ccc">mdi-account</v-icon>
                  </v-avatar>
                  <div>
                    <div class="partner-name">
                      {{ servicePartner.user_name }}
                    </div>
                    <div class="partner-meta">
                      <v-rating
                        :value="servicePartner.rating || 0"
                        dense
                        half-increments
                        readonly
                        size="18"
                        color="#f46523"
                        class="mr-2"
                      />
                      <span class="partner-jobs"
                        >{{ servicePartner.paidBookingCount || 0 }} jobs
                        completed</span
                      >
                    </div>
                  </div>
                </div>
                <v-btn text color="#f46523" @click="openPartnerForm" small>
                  Change
                </v-btn>
              </div>

              <v-btn
                v-else
                outlined
                block
                :color="canSelectPartner ? '#f46523' : '#e0e0e0'"
                :dark="canSelectPartner"
                class="select-partner-btn"
                :disabled="!canSelectPartner"
                @click="handlePartnerClick"
              >
                <v-icon left>mdi-account-multiple</v-icon>
                {{
                  partnerFormExpanded
                    ? "Close Selection"
                    : "Choose Service Partner"
                }}
              </v-btn>

              <!-- Partner Selection Panel -->
              <div ref="partnerFormSectionRef">
                <v-expand-transition>
                  <v-card
                    v-if="partnerFormExpanded"
                    outlined
                    class="selection-panel mt-3"
                  >
                    <div class="panel-header">
                      <h3 class="panel-title">Available Service Partners</h3>
                      <v-btn icon small @click="collapsePartnerForm">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </div>

                    <v-divider />

                    <div class="panel-content">
                      <!-- Loading State -->
                      <div v-if="loadingPartners" class="loading-state">
                        <v-progress-circular
                          indeterminate
                          color="#f46523"
                          size="32"
                        />
                        <p class="loading-text">Loading service partners...</p>
                      </div>

                      <!-- Partner List -->
                      <v-radio-group
                        v-else-if="servicePartners.length > 0"
                        v-model="selectedPartnerId"
                        class="partner-list"
                        color="#f46523"
                      >
                        <div
                          v-for="partner in servicePartners"
                          :key="partner._id"
                          class="partner-card"
                          :class="{
                            'partner-selected-card':
                              selectedPartnerId === partner._id,
                          }"
                          @click="selectPartner(partner)"
                        >
                          <v-radio
                            :value="partner._id"
                            class="partner-radio"
                            color="#f46523"
                          />
                          <v-avatar size="50" color="#f5f5f5" class="mr-3">
                            <v-img
                              v-if="partner.profile_picture"
                              :src="partner.profile_picture"
                              alt="Partner"
                            />
                            <v-icon v-else color="#ccc">mdi-account</v-icon>
                          </v-avatar>
                          <div class="partner-details">
                            <div class="partner-name-row">
                              <strong class="partner-title">{{
                                partner.user_name || partner.name
                              }}</strong>
                              <v-rating
                                :value="partner.rating || 0"
                                dense
                                half-increments
                                readonly
                                size="18"
                                color="#f46523"
                                class="ml-2"
                              />
                            </div>
                            <div class="partner-info-row">
                              <span class="info-item">
                                <v-icon x-small class="mr-1"
                                  >mdi-map-marker</v-icon
                                >
                                {{ partner.city }}
                              </span>
                              <span class="info-divider">•</span>
                              <span class="info-item"
                                >{{ partner.distance || 0 }}m away</span
                              >
                              <span class="info-divider">•</span>
                              <span class="info-item"
                                >{{ partner.paidBookingCount || 0 }} jobs</span
                              >
                            </div>
                          </div>
                        </div>
                      </v-radio-group>

                      <!-- No Partners Available -->
                      <v-alert v-else type="info" text class="ma-0">
                        <div class="alert-content">
                          <div>
                            <div class="alert-title">
                              No service partners available
                            </div>
                            <div class="alert-text">
                              There are no service partners in your selected
                              area at the moment. Try changing your address.
                            </div>
                          </div>
                        </div>
                      </v-alert>
                    </div>

                    <v-divider />
                  </v-card>
                </v-expand-transition>
              </div>
            </div>

            <v-divider class="section-divider" />

            <!-- Step 3: Service Address - COMPACT VERSION -->
            <div class="booking-section">
              <div
                class="step-header"
                :class="{
                  'step-completed': isStep3Completed,
                  'step-disabled': !isStep2Completed,
                }"
              >
                <div class="step-number">
                  <v-icon v-if="isStep3Completed" color="white" small
                    >mdi-check</v-icon
                  >
                  <span v-else>3</span>
                </div>
                <div class="step-content">
                  <h2 class="step-title">Service Location</h2>
                  <p class="step-description">
                    Where should we provide the service?
                  </p>
                </div>
              </div>

              <!-- Loading State -->
              <div v-if="loading" class="loading-state">
                <v-progress-circular indeterminate color="#f46523" size="32" />
                <p class="loading-text">Loading your addresses...</p>
              </div>

              <!-- Address List - COMPACT DESIGN -->
              <div v-else-if="addresses.length > 0">
                <v-radio-group
                  v-model="selectedAddressId"
                  @change="selectAddressApi"
                  class="address-list"
                  color="#f46523"
                >
                  <div
                    v-for="address in addresses"
                    :key="address._id"
                    class="address-card"
                    :class="{
                      'address-selected-card':
                        selectedAddressId === address._id,
                    }"
                  >
                    <!-- Normal Address View -->
                    <div
                      v-if="!showDeleteConfirmation[address._id]"
                      class="address-card-content"
                    >
                      <div class="address-header">
                        <div class="address-type-wrapper">
                          <div
                            class="address-type-icon"
                            :style="{
                              backgroundColor: getAddressTypeColor(
                                address.title
                              ),
                            }"
                          >
                            <v-icon x-small color="white">
                              {{ getAddressTypeIcon(address.title) }}
                            </v-icon>
                          </div>
                          <div class="address-info-compact">
                            <span class="address-type-text">{{
                              address.title
                            }}</span>
                            <span class="address-contact-compact">
                              {{ address.name }} •
                              {{ formatPhone(address.contact_number) }}
                            </span>
                          </div>
                        </div>
                        <v-radio
                          :value="address._id"
                          class="address-radio"
                          color="#f46523"
                          hide-details
                          @click="selectedAddressId = address._id"
                        />
                      </div>

                      <div class="address-details-compact">
                        <div class="address-location-line">
                          <v-icon x-small class="mr-1" color="#666"
                            >mdi-map-marker</v-icon
                          >
                          <span class="address-text-compact">
                            {{ address.address_line_1 }}
                          </span>
                        </div>
                        <div class="address-meta-line">
                          <span
                            v-if="address.landmark"
                            class="address-landmark"
                          >
                            • {{ address.landmark }}
                          </span>
                          <span class="address-city">• {{ address.city }}</span>
                        </div>
                      </div>

                      <div class="address-actions-compact">
                        <v-btn
                          outlined
                          x-small
                          color="#f46523"
                          class="action-btn-compact edit-btn"
                          @click.stop="openEditAddressDialog(address)"
                        >
                          <v-icon x-small color="#f46523"
                            >mdi-pencil-outline</v-icon
                          >
                          <span class="action-text">Edit</span>
                        </v-btn>
                        <v-btn
                          outlined
                          x-small
                          color="#d32f2f"
                          class="action-btn-compact delete-btn"
                          @click.stop="showDeleteConfirmationSection(address)"
                        >
                          <v-icon x-small color="#d32f2f"
                            >mdi-delete-outline</v-icon
                          >
                          <span class="action-text">Delete</span>
                        </v-btn>
                      </div>
                    </div>

                    <!-- Delete Confirmation View -->
                    <div v-else class="delete-confirmation-section">
                      <div class="delete-header">
                        <v-icon color="error" small class="mr-2"
                          >mdi-alert-circle-outline</v-icon
                        >
                        <span class="delete-title">Delete Address?</span>
                      </div>
                      <p class="delete-message">
                        Are you sure you want to delete this address? This
                        action cannot be undone.
                      </p>
                      <div class="delete-actions">
                        <v-btn
                          text
                          small
                          color="#666"
                          class="cancel-delete-btn"
                          @click="cancelDelete(address)"
                        >
                          Cancel
                        </v-btn>
                        <v-btn
                          color="error"
                          dark
                          depressed
                          small
                          class="confirm-delete-btn"
                          @click="deleteAddress(address)"
                          :loading="deletingAddressId === address._id"
                        >
                          <v-icon left small>mdi-delete</v-icon>
                          Delete
                        </v-btn>
                      </div>
                    </div>
                  </div>
                </v-radio-group>

                <v-btn
                  outlined
                  color="#f46523"
                  class="mt-3 add-address-btn-compact"
                  @click="toggleAddressForm"
                  block
                >
                  <v-icon left small>mdi-plus</v-icon>
                  {{ addressFormExpanded ? "Cancel" : "Add New Address" }}
                </v-btn>
              </div>

              <!-- No Address State -->
              <div v-else class="no-address-state-compact">
                <div class="no-address-content">
                  <v-icon size="48" color="#e0e0e0" class="mb-2"
                    >mdi-home-outline</v-icon
                  >
                  <h3 class="no-address-title">No saved addresses</h3>
                  <p class="no-address-text">
                    Add a service address to continue booking
                  </p>
                  <v-btn
                    color="#f46523"
                    dark
                    depressed
                    class="mt-3 white--text add-address-primary-btn"
                    @click="openAddAddressForm"
                    small
                  >
                    <v-icon left small>mdi-plus</v-icon>
                    Add Address
                  </v-btn>
                </div>
              </div>

              <!-- Selection Summary for Address -->
              <div v-if="selectedAddress" class="selection-summary-pill mt-4">
                <div class="summary-icon">
                  <v-icon color="white" x-small>mdi-map-marker-check</v-icon>
                </div>
                <span class="summary-text">
                  Service at <strong>{{ selectedAddress.title }}</strong
                  >: {{ selectedAddress.address_line_1 }}
                </span>
              </div>

              <!-- Address Form Panel -->
              <div ref="addressFormSectionRef">
                <v-expand-transition>
                  <v-card
                    v-if="addressFormExpanded"
                    outlined
                    class="address-form-panel mt-3"
                  >
                    <div class="panel-header">
                      <h3 class="panel-title">
                        {{ isEditMode ? "Edit Address" : "Add New Address" }}
                      </h3>
                      <v-btn icon small @click="collapseAddressForm">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </div>

                    <v-divider />

                    <div class="panel-content">
                      <v-form ref="addressForm" v-model="addressFormValid">
                        <v-row dense>
                          <v-col cols="12" sm="6">
                            <v-text-field
                              v-model="addressForm.name"
                              label="Full Name *"
                              outlined
                              dense
                              :rules="[(v) => !!v || 'Name is required']"
                              hide-details="auto"
                              class="form-field-compact"
                            />
                          </v-col>
                          <v-col cols="12" sm="6">
                            <v-text-field
                              v-model="addressForm.contact_number"
                              label="Contact Number *"
                              outlined
                              dense
                              type="tel"
                              maxlength="10"
                              :rules="[
                                (v) => !!v || 'Contact number is required',
                                (v) =>
                                  /^[0-9]{10}$/.test(v) || 'Must be 10 digits',
                              ]"
                              hide-details="auto"
                              class="form-field-compact"
                            />
                          </v-col>
                          <v-col cols="12">
                            <v-btn
                              small
                              text
                              color="#f46523"
                              class="detect-location-btn mb-2"
                              @click="detectCurrentLocation"
                              :loading="detectingLocation"
                            >
                              <v-icon left small>mdi-crosshairs-gps</v-icon>
                              Use Current Location
                            </v-btn>
                            <v-text-field
                              v-model="addressForm.address_line_1"
                              label="Complete Address *"
                              outlined
                              dense
                              :rules="[(v) => !!v || 'Address is required']"
                              hide-details="auto"
                              class="form-field-compact"
                            />
                          </v-col>
                          <v-col cols="12" sm="6">
                            <v-text-field
                              v-model="addressForm.landmark"
                              label="Landmark *"
                              outlined
                              dense
                              :rules="[(v) => !!v || 'Landmark is required']"
                              hide-details="auto"
                              class="form-field-compact"
                            />
                          </v-col>
                          <v-col cols="12" sm="6">
                            <v-text-field
                              v-model="addressForm.city"
                              label="City *"
                              outlined
                              dense
                              :rules="[(v) => !!v || 'City is required']"
                              hide-details="auto"
                              class="form-field-compact"
                            />
                          </v-col>
                          <v-col cols="12">
                            <div class="address-type-selector-compact">
                              <div class="address-type-label">
                                Address Type *
                              </div>
                              <div class="address-type-options">
                                <div
                                  v-for="type in ['Home', 'Work', 'Other']"
                                  :key="type"
                                  class="address-type-option-compact"
                                  :class="{
                                    'address-type-selected':
                                      addressForm.title === type,
                                  }"
                                  @click="addressForm.title = type"
                                >
                                  <div
                                    class="type-option-icon-compact"
                                    :style="{
                                      backgroundColor:
                                        getAddressTypeColor(type),
                                    }"
                                  >
                                    <v-icon x-small color="white">
                                      {{ getAddressTypeIcon(type) }}
                                    </v-icon>
                                  </div>
                                  <span class="type-option-text">{{
                                    type
                                  }}</span>
                                </div>
                              </div>
                            </div>
                          </v-col>
                          <v-col cols="12">
                            <v-checkbox
                              v-model="addressForm.is_selected"
                              label="Set as default address"
                              hide-details
                              class="mt-0 default-address-checkbox"
                              color="#f46523"
                              dense
                            />
                          </v-col>
                        </v-row>
                      </v-form>
                    </div>

                    <v-divider />

                    <div class="panel-footer-compact">
                      <v-btn
                        text
                        @click="collapseAddressForm"
                        class="cancel-btn"
                        small
                      >
                        Cancel
                      </v-btn>
                      <v-btn
                        small
                        depressed
                        class="save-btn"
                        :style="
                          isSaveDisabled ? disabledBtnStyle : enabledBtnStyle
                        "
                        :dark="!isSaveDisabled"
                        @click="saveAddress"
                        :loading="savingAddress"
                        :disabled="isSaveDisabled"
                      >
                        {{ isEditMode ? "Update" : "Save" }}
                      </v-btn>
                    </div>
                  </v-card>
                </v-expand-transition>
              </div>

              <v-divider class="section-divider" />

              <!-- Step 4: Special Instructions -->
              <div class="booking-section">
                <div class="step-header">
                  <div class="step-number">4</div>
                  <div class="step-content">
                    <h2 class="step-title">Special Instructions</h2>
                    <p class="step-description">
                      Add any specific notes for the service (optional)
                    </p>
                  </div>
                </div>

                <v-textarea
                  v-model="bookingDescription"
                  outlined
                  dense
                  placeholder="e.g. Please bring extra cleaning supplies, Call before arriving, etc."
                  color="#f46523"
                  rows="3"
                  hide-details
                  class="mt-3 special-instructions-field"
                ></v-textarea>
              </div>
            </div>
          </v-card>
        </v-col>

        <!-- Right Section - Payment Summary -->
        <v-col cols="12" md="4">
          <div class="payment-sticky">
            <!-- Coupon Section -->
            <v-card
              class="coupon-card elevation-1 mb-3"
              @click="openCouponDialog"
            >
              <div class="coupon-content">
                <v-icon color="#f46523" size="28">mdi-ticket-percent</v-icon>
                <div class="coupon-text">
                  <div class="coupon-title">Have a coupon code?</div>
                  <div class="coupon-subtitle">Click to apply discounts</div>
                </div>
                <v-icon>mdi-chevron-right</v-icon>
              </div>
            </v-card>

            <!-- Payment Summary -->
            <v-card class="payment-card elevation-2">
              <div class="payment-header">
                <h2 class="payment-title">Booking Summary</h2>
              </div>

              <v-divider class="my-3" />

              <div class="payment-content">
                <div class="payment-row">
                  <span class="payment-label">Service Amount</span>
                  <span class="payment-value">₹{{ payment.totalAmount }}</span>
                </div>

                <div
                  class="payment-row discount-row"
                  v-if="payment.membershipOffer > 0"
                >
                  <span class="payment-label">
                    <v-icon x-small color="success" class="mr-1"
                      >mdi-tag</v-icon
                    >
                    Membership Discount
                  </span>
                  <span class="payment-value discount"
                    >-₹{{ payment.membershipOffer }}</span
                  >
                </div>

                <div
                  class="payment-row discount-row"
                  v-if="payment.couponDiscount > 0"
                >
                  <span class="payment-label">
                    <v-icon x-small color="success" class="mr-1"
                      >mdi-ticket-percent</v-icon
                    >
                    Coupon Discount
                  </span>
                  <span class="payment-value discount"
                    >-₹{{ payment.couponDiscount }}</span
                  >
                </div>

                <div class="payment-row" v-if="payment.platformFee > 0">
                  <span class="payment-label">Platform Fee</span>
                  <span class="payment-value">₹{{ payment.platformFee }}</span>
                </div>

                <v-divider class="my-4" />

                <div class="payment-total">
                  <span class="total-label">Total Payable</span>
                  <span class="total-amount">₹{{ totalPayment }}</span>
                </div>

                <!-- Cancellation Policy -->
                <div class="policy-box">
                  <div class="policy-header">
                    <v-icon small color="#f46523" class="mr-1"
                      >mdi-information</v-icon
                    >
                    <span class="policy-title">Cancellation Policy</span>
                  </div>
                  <p class="policy-text">
                    100% refund if cancelled within 1 hour of booking. No refund
                    after 1 hour.
                  </p>
                </div>

                <!-- Confirm Button -->
                <v-btn
                  block
                  x-large
                  :color="isConfirmDisabled ? '#f5f5f5' : '#f46523'"
                  :dark="!isConfirmDisabled"
                  depressed
                  class="confirm-btn"
                  @click="confirmBooking"
                  :disabled="isConfirmDisabled"
                  :loading="confirmLoading"
                >
                  <v-icon left>mdi-check-circle</v-icon>
                  Confirm Booking
                </v-btn>

                <div v-if="isConfirmDisabled" class="missing-steps-hint mt-3">
                  <v-icon x-small color="#999" class="mr-1"
                    >mdi-information-outline</v-icon
                  >
                  <span>Complete all steps to confirm your booking</span>
                </div>

                <p class="booking-note">
                  By confirming, you agree to our terms and conditions
                </p>
              </div>
            </v-card>
          </div>
        </v-col>
      </v-row>

      <!-- Booking Success Dialog -->
      <v-dialog v-model="bookingSuccessDialog" max-width="480" persistent>
        <v-card class="success-dialog-modern">
          <div class="success-header-graphic">
            <div class="success-icon-wrapper">
              <v-icon size="80" color="white">mdi-check-bold</v-icon>
            </div>
            <div class="confetti-particles"></div>
          </div>

          <v-card-text class="text-center pt-8 px-6 pb-4">
            <h2 class="success-title">Success!</h2>
            <h3 class="success-booking-id">
              Booking #{{ Math.floor(Math.random() * 9000) + 1000 }} confirmed
            </h3>

            <p class="success-subtitle mt-4">
              Your service request has been received. Our partner will arrive at
              the scheduled time.
            </p>

            <v-divider class="my-6" />

            <div class="booking-details-summary">
              <div class="detail-item">
                <v-icon small color="#666" class="mr-2">mdi-calendar</v-icon>
                <span class="detail-label">Date:</span>
                <span class="detail-value">{{
                  selectedDate === "Other" ? customDate : selectedDate
                }}</span>
              </div>
              <div class="detail-item mt-2">
                <v-icon small color="#666" class="mr-2"
                  >mdi-clock-outline</v-icon
                >
                <span class="detail-label">Time:</span>
                <span class="detail-value">{{ selectedTime }}</span>
              </div>
              <div v-if="servicePartner" class="detail-item mt-2">
                <v-icon small color="#666" class="mr-2"
                  >mdi-account-check</v-icon
                >
                <span class="detail-label">Partner:</span>
                <span class="detail-value">{{ servicePartner.name }}</span>
              </div>
            </div>
          </v-card-text>

          <v-card-actions class="justify-center flex-column pa-6 pt-0">
            <v-btn
              block
              color="#f46523"
              dark
              x-large
              depressed
              class="success-action-btn mb-3"
              @click="goToBookings"
            >
              Go to Bookings
            </v-btn>
            <v-btn
              text
              color="grey darken-1"
              class="close-dialog-btn"
              @click="goToHome"
            >
              Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import {
  getAllAddresses,
  selectAddress,
  addAddress,
  editAddress,
  deleteAddress,
} from "../services/address.service";
import { addToCart, createBooking } from "../services/booking.service";
import gsap from "gsap";
import { getApiBaseUrl } from "../config/api";

export default {
  name: "AddBookings",

  data() {
    return {
      selectedDate: "Today",
      customDate: null,
      disabledBtnStyle: {
        backgroundColor: "#e0e0e0",
        color: "#9e9e9e",
        boxShadow: "none",
      },

      enabledBtnStyle: {
        backgroundColor: "#f46523",
        color: "#ffffff",
      },

      selectedAddressId: null,
      selectedServiceTypeId: null,
      serviceName: "Service",
      servicePartner: null,
      bookingSuccessDialog: false,

      partnerFormExpanded: false,
      servicePartners: [],
      confirmLoading: false,
      bookingDescription: "",

      snackbar: {
        show: false,
        message: "",
        color: "success",
      },

      selectedPartnerId: null,
      loadingPartners: false,

      addresses: [],
      selectedAddress: null,
      loading: false,

      addressFormExpanded: false,
      isEditMode: false,
      savingAddress: false,
      tempSelectedAddressId: null,
      editingAddressId: null,

      addressFormValid: false,
      addressForm: {
        name: "",
        title: "Home",
        landmark: "",
        address_line_1: "",
        city: "",
        latitude: null,
        longitude: null,
        contact_number: "",
        is_selected: false,
      },

      // Delete confirmation state
      showDeleteConfirmation: {},
      deletingAddressId: null,
      detectingLocation: false,

      payment: {
        totalAmount: 300,
        membershipOffer: 0,
        couponDiscount: 0,
        platformFee: 0,
      },

      selectedTime: null,
      timeSheet: false,
      generatedTimeSlots: [],
    };
  },

  computed: {
    isSaveDisabled() {
      return !this.addressFormValid || this.savingAddress;
    },
    canSelectPartner() {
      return this.selectedDate === "Today";
    },

    totalPayment() {
      return (
        this.payment.totalAmount -
        this.payment.membershipOffer -
        this.payment.couponDiscount +
        this.payment.platformFee
      );
    },

    isStep1Completed() {
      return !!(this.selectedDate && this.selectedTime);
    },

    isStep2Completed() {
      return this.isStep1Completed; // partner never mandatory
    },

    isStep3Completed() {
      return this.isStep2Completed && !!this.selectedAddress;
    },

    isConfirmDisabled() {
      return !this.isStep3Completed;
    },
  },

  created() {
    let payload = this.$route.params;

    if (!payload?.serviceTypeId) {
      payload = JSON.parse(sessionStorage.getItem("bookingPayload") || "null");
    }

    if (!payload?.serviceTypeId) {
      this.$router.replace("/services");
      return;
    }

    this.selectedServiceTypeId = payload.serviceTypeId;
    this.serviceName = payload.serviceName || "Service";

    this.fetchAddresses();
    this.fetchServiceSelection();
    this.generateTimeSlots();
  },

  watch: {
    selectedDate(newVal) {
      if (newVal !== "Today") {
        this.servicePartner = null;
        this.selectedPartnerId = null;
        this.partnerFormExpanded = false;
      }
    },
  },

  methods: {
    getTimeZoneParts(date = new Date()) {
      const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Asia/Kolkata",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).formatToParts(date);
      const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
      return {
        year: Number(map.year),
        month: Number(map.month),
        day: Number(map.day),
        hour: Number(map.hour),
        minute: Number(map.minute),
      };
    },
    formatDateFromParts(parts) {
      const mm = String(parts.month).padStart(2, "0");
      const dd = String(parts.day).padStart(2, "0");
      return `${parts.year}-${mm}-${dd}`;
    },
    addMinutesToTime(timeStr, minutesToAdd) {
      if (!timeStr) return timeStr;
      const [time, period] = timeStr.split(" ");
      let [hours, minutes] = time.split(":").map((v) => parseInt(v, 10));
      if (period === "PM" && hours !== 12) hours += 12;
      if (period === "AM" && hours === 12) hours = 0;

      let total = hours * 60 + minutes + minutesToAdd;
      total = ((total % (24 * 60)) + 24 * 60) % (24 * 60);
      const newHours24 = Math.floor(total / 60);
      const newMinutes = total % 60;
      const newPeriod = newHours24 >= 12 ? "PM" : "AM";
      const newHours12 = newHours24 % 12 || 12;
      return `${newHours12}:${String(newMinutes).padStart(
        2,
        "0"
      )} ${newPeriod}`;
    },
    selectPartner(partner) {
      if (!partner) return;

      this.selectedPartnerId = partner._id;
      this.servicePartner = partner;

      // small delay for better UX animation feel
      setTimeout(() => {
        this.collapsePartnerForm();
      }, 150);
    },

    handlePartnerClick() {
      if (!this.canSelectPartner) {
        this.showSnackbar(
          "Service partner selection is available only for Today bookings.",
          "error"
        );
        return;
      }

      this.togglePartnerForm();
    },

    generateTimeSlots() {
      const slots = [];
      const startHour = 8; // 8 AM
      const endHour = 20; // 8 PM

      for (let h = startHour; h <= endHour; h++) {
        for (let m = 0; m < 60; m += 30) {
          const period = h >= 12 ? "PM" : "AM";
          const hour12 = h % 12 || 12;
          const minuteStr = m === 0 ? "00" : m;
          slots.push(`${hour12}:${minuteStr} ${period}`);
        }
      }
      this.generatedTimeSlots = slots;
    },

    isTimeDisabled(timeStr) {
      if (this.selectedDate !== "Today") return false;

      const nowParts = this.getTimeZoneParts();
      const currentHour = nowParts.hour;
      const currentMinute = nowParts.minute;

      // Parse time string "09:30 AM"
      const [time, period] = timeStr.split(" ");
      let [hours, minutes] = time.split(":");
      hours = parseInt(hours);
      minutes = parseInt(minutes);

      if (period === "PM" && hours !== 12) hours += 12;
      if (period === "AM" && hours === 12) hours = 0;

      if (hours < currentHour) return true;
      if (hours === currentHour && minutes <= currentMinute) return true;

      return false;
    },

    selectTime(time) {
      if (this.isTimeDisabled(time)) return;
      this.selectedTime = time;
      this.timeSheet = false;
    },

    showSnackbar(message, color = "success") {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },

    // Helper methods for address types
    getAddressTypeColor(type) {
      const colors = {
        Home: "#f46523", // Your brand color for Home
        Work: "#2196F3", // Blue for Work
        Other: "#9C27B0", // Purple for Other
      };
      return colors[type] || "#666";
    },

    getAddressTypeIcon(type) {
      const icons = {
        Home: "mdi-home",
        Work: "mdi-briefcase",
        Other: "mdi-map-marker",
      };
      return icons[type] || "mdi-map-marker";
    },

    formatPhone(phone) {
      if (!phone) return "";
      return phone.length >= 10
        ? `${phone.substring(0, 5)}...${phone.substring(8)}`
        : phone;
    },

    // Show delete confirmation section
    showDeleteConfirmationSection(address) {
      this.$set(this.showDeleteConfirmation, address._id, true);
    },

    // Cancel delete
    cancelDelete(address) {
      this.$set(this.showDeleteConfirmation, address._id, false);
    },

    // Delete address
    async deleteAddress(address) {
      if (!address || !address._id) return;

      this.deletingAddressId = address._id;

      try {
        await deleteAddress(address._id);

        // Close delete confirmation
        this.$set(this.showDeleteConfirmation, address._id, false);

        // Refresh addresses
        await this.fetchAddresses();

        // If deleted address was selected, select the first available address
        if (
          this.selectedAddressId === address._id &&
          this.addresses.length > 0
        ) {
          this.selectedAddressId = this.addresses[0]._id;
          await this.selectAddressApi(this.selectedAddressId);
        }
      } catch (error) {
        console.error("Delete error:", error);
        // Show error message
      } finally {
        this.deletingAddressId = null;
      }
    },

    async fetchServiceSelection() {
      if (!this.selectedServiceTypeId) return;

      const token = localStorage.getItem("access_token");

      this.loadingPartners = true;

      try {
        const res = await fetch(
          `${getApiBaseUrl()}/services-selection/${this.selectedServiceTypeId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        const data = await res.json();

        const partners =
          data?.data?.service_partners ??
          data?.data?.servicePartners ??
          data?.data?.partners ??
          [];
        if (Array.isArray(partners) && partners.length) {
          this.servicePartners = partners;
        } else {
          this.servicePartners = [];
        }

        if (data?.data?.payment_info?.length) {
          const paymentMap = Object.fromEntries(
            data.data.payment_info.map((i) => [i.title, i.value])
          );

          this.payment.totalAmount = Number(paymentMap.total_amount) || 0;
          this.payment.membershipOffer = Number(paymentMap.discount) || 0;
          this.payment.platformFee =
            paymentMap.tax === "N/A" ? 0 : Number(paymentMap.tax) || 0;
        }
      } catch (err) {
        console.error("Service selection API error:", err);
        this.servicePartners = [];
      } finally {
        this.loadingPartners = false;
      }
    },

    togglePartnerForm() {
      if (this.partnerFormExpanded) {
        this.collapsePartnerForm();
      } else {
        this.openPartnerForm();
      }
    },

    openPartnerForm() {
      this.partnerFormExpanded = true;
      this.$nextTick(() => this.scrollToPartnerForm());
    },

    collapsePartnerForm() {
      this.partnerFormExpanded = false;
    },

    scrollToPartnerForm() {
      this.$refs.partnerFormSectionRef?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    },

    async fetchAddresses() {
      this.loading = true;
      try {
        const response = await getAllAddresses();
        this.addresses = response.data?.data || [];

        const defaultAddr =
          this.addresses.find((a) => a.is_selected) || this.addresses[0];

        this.selectedAddress = defaultAddr || null;
        this.selectedAddressId = defaultAddr?._id || null;
      } catch (error) {
        console.error("Error fetching addresses:", error);
      } finally {
        this.loading = false;
      }
    },

    async selectAddressApi(addressId) {
      if (!addressId) return;

      try {
        await selectAddress(addressId);

        // refresh addresses
        await this.fetchAddresses();

        // 🔥 IMPORTANT: refetch partners for new address
        await this.fetchServiceSelection();

        // reset selected partner if no partners available
        if (!this.servicePartners.length) {
          this.selectedPartnerId = null;
          this.servicePartner = null;
        }
      } catch (error) {
        console.error("Error selecting address:", error);
      }
    },

    toggleAddressForm() {
      if (this.addressFormExpanded) {
        this.collapseAddressForm();
      } else {
        this.openAddAddressForm();
      }
    },

    openAddAddressForm() {
      this.resetAddressForm();
      this.isEditMode = false;
      this.editingAddressId = null;
      this.addressFormExpanded = true;
      this.$nextTick(() => {
        if (this.$refs.addressForm) {
          this.$refs.addressForm.resetValidation();
        }
        this.scrollToAddressForm();
      });
    },

    openEditAddressDialog(address) {
      if (!address || !address._id) {
        console.error("Invalid address object", address);
        return;
      }

      this.editingAddressId = address._id;
      this.isEditMode = true;
      this.addressForm = {
        name: address.name || "",
        title: address.title || "Home",
        landmark: address.landmark || "",
        address_line_1: address.address_line_1 || "",
        city: address.city || "",
        latitude: address.latitude || null,
        longitude: address.longitude || null,
        contact_number: address.contact_number || "",
        is_selected: address.is_selected || false,
      };
      this.addressFormExpanded = true;
      this.$nextTick(() => this.scrollToAddressForm());
    },

    collapseAddressForm() {
      this.addressFormExpanded = false;
    },

    scrollToAddressForm() {
      this.$refs.addressFormSectionRef?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    },

    async detectCurrentLocation() {
      if (!navigator.geolocation) {
        this.showSnackbar(
          "Geolocation is not supported by your browser",
          "error"
        );
        return;
      }

      this.detectingLocation = true;

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          this.addressForm.latitude = latitude;
          this.addressForm.longitude = longitude;

          try {
            // Reverse geocoding using Nominatim (OpenStreetMap)
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`
            );
            const data = await response.json();

            if (data && data.address) {
              const addr = data.address;
              // Build a readable address string
              const addressParts = [
                addr.road,
                addr.suburb,
                addr.neighbourhood,
              ].filter(Boolean);

              this.addressForm.address_line_1 =
                addressParts.join(", ") || data.display_name;
              this.addressForm.city =
                addr.city ||
                addr.town ||
                addr.village ||
                addr.state_district ||
                "";

              this.showSnackbar("Location detected successfully!", "success");
            }
          } catch (error) {
            console.error("Reverse geocoding error:", error);
            this.showSnackbar(
              "Location detected, but couldn't fetch address details.",
              "warning"
            );
          } finally {
            this.detectingLocation = false;
          }
        },
        (error) => {
          this.detectingLocation = false;
          let msg = "Error detecting location";
          if (error.code === 1) msg = "Location permission denied";
          else if (error.code === 2) msg = "Location unavailable";
          else if (error.code === 3) msg = "Location request timed out";

          this.showSnackbar(msg, "error");
        },
        { timeout: 10000 }
      );
    },

    resetAddressForm() {
      this.addressForm = {
        name: "",
        title: "Home",
        landmark: "",
        address_line_1: "",
        city: "",
        latitude: null,
        longitude: null,
        contact_number: "",
        is_selected: false,
      };
      this.addressFormValid = false;
    },

    async saveAddress() {
      // Validate form
      if (!this.$refs.addressForm.validate()) return;

      // Get stored location
      const location = this.getLocationFromStorage();
      if (!location) {
        this.showSnackbar(
          "Location not found. Please enable location.",
          "error"
        );
        return;
      }

      // Prepare payload
      const payload = {
        ...this.addressForm,
        latitude: location.latitude,
        longitude: location.longitude,
      };

      this.savingAddress = true;

      try {
        let res;

        /* ---------------------------------- */
        /* ADD or EDIT ADDRESS API CALL       */
        /* ---------------------------------- */
        if (this.isEditMode) {
          res = await editAddress(this.editingAddressId, payload);
        } else {
          res = await addAddress(payload);
        }

        /* ---------------------------------- */
        /* 🔥 HANDLE BUSINESS ERROR RESPONSE  */
        /* (API returns 200 but error_code=1) */
        /* ---------------------------------- */
        if (res?.data?.error_code === 1) {
          this.showSnackbar(
            res.data.message || "Address already exists. Please edit instead.",
            "error"
          );
          return; // stop execution
        }

        /* ---------------------------------- */
        /* ✅ SUCCESS                         */
        /* ---------------------------------- */
        this.showSnackbar(
          this.isEditMode
            ? "Address updated successfully"
            : "Address added successfully",
          "success"
        );

        // Refresh address list
        await this.fetchAddresses();

        // Refetch service partners for new location
        await this.fetchServiceSelection();

        // Reset selected partner (location changed)
        this.servicePartner = null;
        this.selectedPartnerId = null;

        // Close form
        this.collapseAddressForm();
      } catch (error) {
        console.error("Save address failed:", error);

        this.showSnackbar(
          "Something went wrong while saving the address. Please try again.",
          "error"
        );
      } finally {
        this.savingAddress = false;
      }
    },

    openCouponDialog() {
      console.log("Opening coupon dialog");
    },

    getLocationFromStorage() {
      const raw =
        localStorage.getItem("userLocation") ||
        sessionStorage.getItem("userLocation");

      if (!raw) {
        console.error("userLocation not found in storage");
        return null;
      }

      const parsed = JSON.parse(raw);

      if (!parsed.latitude || !parsed.longitude) {
        console.error("Invalid latitude/longitude", parsed);
        return null;
      }

      return {
        latitude: parsed.latitude,
        longitude: parsed.longitude,
      };
    },

    async confirmBooking() {
      if (this.isConfirmDisabled) return;

      this.confirmLoading = true;

      try {
        // 1. Prepare Date
        let requested_date = "";
        const tzNow = this.getTimeZoneParts();
        if (this.selectedDate === "Today") {
          requested_date = this.formatDateFromParts(tzNow);
        } else if (this.selectedDate === "Tomorrow") {
          const base = new Date(
            Date.UTC(tzNow.year, tzNow.month - 1, tzNow.day)
          );
          base.setUTCDate(base.getUTCDate() + 1);
          const t = this.getTimeZoneParts(base);
          requested_date = this.formatDateFromParts(t);
        } else {
          requested_date = this.customDate;
        }
        const requested_time = this.addMinutesToTime(this.selectedTime, 10);

        // 2. Call Cart Add API
        const cartRes = await addToCart({
          serviceTypeId: this.selectedServiceTypeId,
          requested_date: requested_date,
          requested_time: requested_time,
          description: this.bookingDescription || "No specific instructions",
        });

        if (cartRes.data?.error_code !== 0) {
          this.showSnackbar(
            cartRes.data?.message ||
              "Could not add to cart. Please check your selection.",
            "error"
          );
          this.confirmLoading = false;
          return;
        }

        // 3. Call Booking Create API
        const bookingRes = await createBooking({
          description: this.bookingDescription || "No specific instructions",
        });

        if (bookingRes.data?.error_code !== 0) {
          this.showSnackbar(
            bookingRes.data?.message || "Booking failed. Please try again.",
            "error"
          );
          this.confirmLoading = false;
          return;
        }

        // 4. Success handling
        sessionStorage.removeItem("selectedCategory");
        sessionStorage.removeItem("selectedService");

        this.confirmLoading = false;
        this.bookingSuccessDialog = true;
        this.$nextTick(() => {
          this.animateSuccess();
        });
      } catch (error) {
        console.error("Booking process error:", error);
        this.confirmLoading = false;
        this.showSnackbar(
          error.response?.data?.message ||
            "Something went wrong during booking. Please try again.",
          "error"
        );
      }
    },

    goToBookings() {
      this.$router.push("/bookings");
    },

    goToHome() {
      this.$router.push("/");
    },
    animateSuccess() {
      gsap.from(".success-icon-wrapper", {
        scale: 0,
        duration: 0.8,
        ease: "back.out(1.7)",
      });
      gsap.from(".success-title", {
        y: 20,
        opacity: 0,
        duration: 0.5,
        delay: 0.3,
      });
      gsap.from(".success-subtitle", {
        y: 20,
        opacity: 0,
        duration: 0.5,
        delay: 0.4,
      });
    },
  },
};
</script>

<style scoped>
/* ============================================ */
/* BASE STYLES */
/* ============================================ */
.booking-page {
  background: #f5f5f5;
  min-height: 100vh;
  padding-bottom: 40px;
}

/* ============================================ */
/* PAGE HEADER */
/* ============================================ */
.page-header {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
.page-header-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #fff3ed;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 4px 0;
  line-height: 1.2;
}

.service-name {
  font-size: 16px;
  color: #666;
  margin: 0;
  font-weight: 500;
}

/* ============================================ */
/* BOOKING CARD */
/* ============================================ */
.booking-card {
  border-radius: 12px;
  padding: 0;
  overflow: hidden;
}

.booking-section {
  padding: 20px;
}

.section-divider {
  margin: 0;
}

/* ============================================ */
/* STEP HEADER */
/* ============================================ */
.step-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
}

.step-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f46523 0%, #ff8855 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(244, 101, 35, 0.3);
}

.step-content {
  flex: 1;
}

.step-title {
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 4px 0;
  line-height: 1.3;
}

.step-description {
  font-size: 15px;
  color: #666;
  margin: 0;
}
.step-optional {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #fff3ed;
  color: #f46523;
  font-weight: 600;
  font-size: 12px;
}

.step-header.step-completed .step-number {
  background: #4caf50;
  box-shadow: 0 2px 6px rgba(76, 175, 80, 0.3);
}

.step-header.step-disabled {
  opacity: 0.6;
  filter: grayscale(0.5);
  pointer-events: none;
}

.step-header.step-disabled .step-number {
  background: #e0e0e0;
  color: #999;
  box-shadow: none;
}

/* ============================================ */
/* SELECTION SUMMARIES */
/* ============================================ */
.selection-summary-pill {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff8f4;
  border: 1px solid #ffeada;
  padding: 10px 14px;
  border-radius: 8px;
}

.summary-icon {
  width: 20px;
  height: 20px;
  background: #f46523;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.summary-text {
  font-size: 15px;
  color: #444;
}

.summary-text strong {
  color: #f46523;
}

/* ============================================ */
/* DATE & TIME SELECTION - COMPACT */
/* ============================================ */
.datetime-compact {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.compact-group {
  flex: 1;
}

.compact-label {
  font-size: 13px;
  font-weight: 600;
  color: #555;
  margin-bottom: 8px;
  display: block;
}

.compact-buttons {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.compact-btn {
  text-transform: none !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  border-radius: 6px !important;
  font-size: 13px !important;
  height: 32px !important;
  min-width: 70px !important;
  transition: all 0.2s;
}

.compact-btn.active-compact {
  font-weight: 600 !important;
  box-shadow: 0 2px 4px rgba(244, 101, 35, 0.2);
}

/* Time Sheet Styles */
.time-select-btn {
  height: 44px !important;
  border-radius: 8px !important;
  border: 1px solid #e0e0e0 !important;
  text-transform: none !important;
  justify-content: flex-start !important;
  padding: 0 12px !important;
  font-weight: 500 !important;
  color: #333 !important;
  background: white !important;
}

.time-sheet-card {
  border-radius: 16px !important;
  max-height: 80vh;
}

.sheet-header {
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
}

.sheet-title {
  font-size: 17px;
  font-weight: 700;
  color: #1a1a1a;
}

.sheet-content {
  padding: 16px !important;
}

/* Time List Grid */
.time-list-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 12px;
  padding: 4px;
}

.time-slot-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 0;
  background: #f5f5f5;
  border-radius: 8px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  color: #333;
  transition: all 0.2s;
  border: 1px solid transparent;
}

.time-slot-btn:hover:not(.disabled) {
  background: #fff3ed;
  border-color: #f46523;
  color: #f46523;
}

.time-slot-btn.selected {
  background: #f46523;
  color: white;
  border-color: #f46523;
  box-shadow: 0 4px 8px rgba(244, 101, 35, 0.3);
}

.time-slot-btn.disabled {
  opacity: 0.5;
  background: #eee;
  color: #bbb;
  cursor: not-allowed;
  pointer-events: none;
}

/* ============================================ */
/* PARTNER SECTION */
/* ============================================ */
.partner-selected {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: #fff3ed;
  border-radius: 8px;
  border: 1px solid #f46523;
}

.partner-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.partner-name {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 2px;
}

.partner-meta {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #666;
}

.partner-jobs {
  font-size: 14px;
  color: #666;
}

.select-partner-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  height: 40px !important;
  border-radius: 8px !important;
  font-size: 14px !important;
}

/* ============================================ */
/* SELECTION PANELS (Partner & Address Forms) */
/* ============================================ */
.selection-panel {
  border-radius: 8px;
  border: 2px solid #e0e0e0;
  overflow: hidden;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #fafafa;
}

.panel-title {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
}

.panel-content {
  padding: 16px;
  max-height: 400px;
  overflow-y: auto;
}

.panel-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 16px;
  background: #fafafa;
  border-top: 1px solid #e0e0e0;
}

/* ============================================ */
/* PARTNER LIST */
/* ============================================ */
.partner-list {
  margin-top: 0;
}

.partner-card {
  display: flex;
  align-items: flex-start;
  padding: 12px;
  border: 1.5px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.partner-card:hover {
  border-color: #f46523;
  background: #fffbf8;
}

.partner-card.partner-selected-card {
  border-color: #f46523;
  background: #fff3ed;
  box-shadow: 0 2px 6px rgba(244, 101, 35, 0.1);
}

.partner-radio {
  margin-right: 10px;
  margin-top: 2px;
}

.partner-details {
  flex: 1;
}

.partner-name-row {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.partner-title {
  font-size: 16px;
  color: #1a1a1a;
}

.partner-info-row {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #666;
  flex-wrap: wrap;
  gap: 6px;
}

.info-item {
  display: flex;
  align-items: center;
}

.info-divider {
  color: #ccc;
}

/* ============================================ */
/* COMPACT ADDRESS SECTION */
/* ============================================ */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  text-align: center;
  padding: 30px 20px;
}

.loading-text {
  margin-top: 12px;
  color: #666;
  font-size: 13px;
}

/* Compact Address Card Design */
.address-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  margin-bottom: 8px;
  overflow: hidden;
  transition: all 0.2s;
}

.address-card:hover {
  border-color: #f46523;
  box-shadow: 0 2px 8px rgba(244, 101, 35, 0.1);
}

.address-card.address-selected-card {
  border-color: #f46523;
  background: linear-gradient(135deg, #fff9f5 0%, #fff3ed 100%);
  box-shadow: 0 4px 12px rgba(244, 101, 35, 0.1);
  position: relative;
}

.address-card.address-selected-card::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(to bottom, #f46523, #ff8855);
}

.address-card-content {
  padding: 10px 12px;
  cursor: pointer;
}

/* Address Header - Compact */
.address-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 6px;
}

.address-type-wrapper {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex: 1;
}

.address-type-icon {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
}

.address-info-compact {
  flex: 1;
  min-width: 0;
}

.address-type-text {
  font-size: 14px;
  font-weight: 700;
  color: #222;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  display: block;
  margin-bottom: 3px;
}

.address-contact-compact {
  font-size: 14px;
  color: #4b5563;
  font-weight: 600;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.address-radio {
  margin: 0;
  padding: 0;
}

/* Address Details - Compact */
.address-details-compact {
  margin-bottom: 6px;
  padding-left: 2px;
}

.address-location-line {
  display: flex;
  align-items: flex-start;
  margin-bottom: 3px;
}

.address-text-compact {
  font-size: 15px;
  color: #374151;
  line-height: 1.45;
  flex: 1;
}

.address-meta-line {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #6b7280;
  padding-left: 18px;
}

.address-landmark {
  font-style: italic;
}

.address-city {
  font-weight: 500;
}

/* Address Actions - Compact */
.address-actions-compact {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
  justify-content: flex-end;
}

.action-btn-compact {
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  letter-spacing: 0.3px !important;
  min-width: 72px !important;
  padding: 0 10px !important;
  height: 28px !important;
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 6px !important;
  transition: all 0.2s ease;
  border: 1px solid currentColor !important;
}

.action-btn-compact.edit-btn:hover {
  background-color: #fff3ed !important;
}

.action-btn-compact.delete-btn:hover {
  background-color: #fff5f5 !important;
}

.action-text {
  font-size: 12px;
}

/* Delete Confirmation Section */
.delete-confirmation-section {
  padding: 16px;
  background: #fff5f5;
  border-left: 3px solid #f44336;
}

.delete-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.delete-title {
  font-size: 14px;
  font-weight: 600;
  color: #d32f2f;
}

.delete-message {
  font-size: 13px;
  color: #666;
  line-height: 1.4;
  margin-bottom: 12px;
}

.delete-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.cancel-delete-btn {
  text-transform: none !important;
  font-weight: 500 !important;
  font-size: 12px !important;
  height: 28px !important;
}

.confirm-delete-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  height: 28px !important;
  border-radius: 4px !important;
  background: linear-gradient(135deg, #f44336 0%, #ef5350 100%) !important;
}

/* Add Address Button - Compact */
.add-address-btn-compact {
  text-transform: none !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  height: 36px !important;
  border: 1px dashed #f46523 !important;
  color: #f46523 !important;
  font-size: 13px !important;
}

.add-address-btn-compact:hover {
  background: #fff3ed !important;
}

/* No Address State - Compact */
.no-address-state-compact {
  text-align: center;
  padding: 30px 16px;
  background: white;
  border: 1px dashed #e0e0e0;
  border-radius: 10px;
}

.no-address-content {
  max-width: 280px;
  margin: 0 auto;
}

.no-address-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
}

.no-address-text {
  font-size: 13px;
  color: #666;
  margin-bottom: 16px;
}

.add-address-primary-btn {
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  height: 36px !important;
  font-size: 13px !important;
}

/* Address Form Panel - Compact */
.address-form-panel {
  border-radius: 10px;
  border: 1px solid #e0e0e0;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
}

.form-field-compact >>> .v-label {
  font-size: 13px !important;
}

.form-field-compact >>> .v-input__control {
  min-height: 40px !important;
}

/* Compact Address Type Selector */
.address-type-selector-compact {
  margin-bottom: 12px;
}

.address-type-label {
  font-size: 12px;
  font-weight: 600;
  color: #555;
  margin-bottom: 8px;
}

.address-type-options {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.address-type-option-compact {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1.5px solid #e0e0e0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  background: white;
  flex: 1;
  min-width: 80px;
  justify-content: center;
}

.address-type-option-compact:hover {
  border-color: #f46523;
  background: #fffbf8;
}

.address-type-option-compact.address-type-selected {
  border-color: #f46523;
  background: #fff3ed;
  box-shadow: 0 1px 4px rgba(244, 101, 35, 0.1);
}

.type-option-icon-compact {
  width: 22px;
  height: 22px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.type-option-text {
  font-size: 13px;
  font-weight: 500;
  color: #333;
}

.default-address-checkbox >>> .v-label {
  font-size: 13px !important;
  color: #555 !important;
}

/* Compact Panel Footer */
.panel-footer-compact {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  background: #fafafa;
  border-top: 1px solid #e0e0e0;
}

.cancel-btn {
  text-transform: none !important;
  font-weight: 500 !important;
  color: #666 !important;
  font-size: 13px !important;
  height: 32px !important;
}

.save-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  border-radius: 6px !important;
  padding: 0 16px !important;
  font-size: 13px !important;
  height: 32px !important;
}

/* ============================================ */
/* PAYMENT SECTION */
/* ============================================ */
.payment-sticky {
  position: sticky;
  top: 20px;
}

.coupon-card {
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #e0e0e0;
  margin-bottom: 12px;
}

.coupon-card:hover {
  border-color: #f46523;
  box-shadow: 0 3px 8px rgba(244, 101, 35, 0.1);
}

.coupon-content {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px;
}

.coupon-text {
  flex: 1;
}

.coupon-title {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 2px;
}

.coupon-subtitle {
  font-size: 12px;
  color: #666;
}

.payment-card {
  border-radius: 12px;
  padding: 20px;
}

.payment-header {
  margin-bottom: 6px;
}

.payment-title {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
}

.payment-content {
  padding-top: 6px;
}

.payment-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.payment-label {
  font-size: 13px;
  color: #555;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.payment-value {
  font-size: 14px;
  color: #1a1a1a;
  font-weight: 600;
}

.payment-value.discount {
  color: #4caf50;
  font-size: 13px;
}

.discount-row .payment-label {
  color: #4caf50;
  font-size: 13px;
}

.payment-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-top: 1px solid #e0e0e0;
  margin-top: 8px;
}

.total-label {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
}

.total-amount {
  font-size: 20px;
  font-weight: 700;
  color: #f46523;
}

.policy-box {
  background: #fff3ed;
  border-left: 3px solid #f46523;
  padding: 12px;
  border-radius: 6px;
  margin: 16px 0;
}

.policy-header {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.policy-title {
  font-size: 12px;
  font-weight: 700;
  color: #f46523;
}

.policy-text {
  font-size: 12px;
  color: #555;
  line-height: 1.5;
  margin: 0;
}

.confirm-btn {
  text-transform: none !important;
  font-weight: 700 !important;
  font-size: 15px !important;
  letter-spacing: 0.3px !important;
  border-radius: 8px !important;
  height: 48px !important;
  box-shadow: 0 3px 8px rgba(244, 101, 35, 0.2) !important;
  margin-top: 16px;
}

.confirm-btn:hover {
  box-shadow: 0 4px 12px rgba(244, 101, 35, 0.3) !important;
}

.confirm-btn:disabled {
  background: #ccc !important;
  box-shadow: none !important;
}

.booking-note {
  text-align: center;
  font-size: 11px;
  color: #999;
  margin: 10px 0 0 0;
}

.missing-steps-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #777;
  background: #f9f9f9;
  padding: 8px;
  border-radius: 6px;
}

.auto-assign-note {
  border-radius: 8px !important;
  font-size: 13px !important;
}

/* ============================================ */
/* RESPONSIVE */
/* ============================================ */
@media (max-width: 960px) {
  .datetime-compact {
    flex-direction: column;
    gap: 16px;
  }

  .payment-sticky {
    position: static;
  }
}

@media (max-width: 600px) {
  .booking-page {
    padding: 12px;
  }

  .page-header {
    padding: 16px;
  }

  .page-title {
    font-size: 22px;
  }

  .booking-section {
    padding: 16px;
  }

  .step-header {
    gap: 10px;
  }

  .step-number {
    width: 28px;
    height: 28px;
    font-size: 13px;
  }

  .step-title {
    font-size: 18px;
  }

  .step-description {
    font-size: 13px;
  }

  .datetime-compact {
    gap: 12px;
  }

  .compact-buttons {
    gap: 4px;
  }

  .compact-btn {
    font-size: 12px !important;
    height: 30px !important;
    min-width: 65px !important;
  }

  .address-card-content,
  .delete-confirmation-section {
    padding: 10px;
  }

  .address-type-icon {
    width: 24px;
    height: 24px;
  }

  .address-type-text {
    font-size: 12px;
  }

  .address-contact-compact {
    font-size: 12px;
  }

  .address-text-compact {
    font-size: 13px;
  }

  .address-meta-line {
    font-size: 12px;
  }

  .action-text {
    display: inline;
    font-size: 12px;
  }

  .delete-title {
    font-size: 13px;
  }

  .delete-message {
    font-size: 12px;
  }

  .payment-card {
    padding: 16px;
  }

  .payment-title {
    font-size: 17px;
  }

  .payment-label {
    font-size: 13px;
  }

  .payment-value {
    font-size: 14px;
  }

  .total-label {
    font-size: 16px;
  }

  .total-amount {
    font-size: 19px;
  }

  .confirm-btn {
    height: 44px !important;
    font-size: 14px !important;
  }
}

@media (max-width: 400px) {
  .address-type-options {
    flex-direction: column;
  }

  .address-type-option-compact {
    min-width: 100%;
    justify-content: flex-start;
  }

  .delete-actions {
    flex-direction: column;
    gap: 6px;
  }

  .cancel-delete-btn,
  .confirm-delete-btn {
    width: 100%;
    justify-content: center;
  }
}

.global-snackbar {
  z-index: 9999 !important;
}

/* Confirm button disabled state → light gray */
.confirm-btn.v-btn--disabled {
  background: #e0e0e0 !important; /* light gray */
  color: #9e9e9e !important; /* soft text gray */
  box-shadow: none !important;
  cursor: not-allowed !important;
}
/* FORCE light gray disabled button */
::v-deep(.confirm-btn.v-btn--disabled) {
  background-color: #e0e0e0 !important;
  color: #9e9e9e !important;
  box-shadow: none !important;
  cursor: not-allowed !important;
  opacity: 1 !important; /* Vuetify reduces opacity by default */
}
</style>
<style scoped>
/* Selection Summary Styles */
.selection-summary {
  background: #fff3ed;
  border: 1px dashed #f46523;
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  animation: fadeIn 0.5s ease-out;
}

.summary-text {
  font-size: 14px;
  color: #333;
}

.summary-text strong {
  color: #f46523;
}

/* Modern Success Dialog Styles */
.success-dialog-modern {
  border-radius: 20px !important;
  overflow: hidden;
}

.success-header-graphic {
  background: linear-gradient(135deg, #f46523 0%, #ff8c52 100%);
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.success-icon-wrapper {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  z-index: 2;
}

.success-title {
  font-size: 28px;
  font-weight: 800;
  color: #1a1a1a;
  margin-bottom: 4px;
}

.success-booking-id {
  font-size: 14px;
  font-weight: 600;
  color: #f46523;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.success-subtitle {
  font-size: 15px;
  color: #666;
  line-height: 1.6;
}

.booking-details-summary {
  background: #f9f9f9;
  border-radius: 12px;
  padding: 16px;
  text-align: left;
}

.detail-item {
  display: flex;
  align-items: center;
}

.detail-label {
  font-size: 13px;
  color: #888;
  width: 70px;
  font-weight: 500;
}

.detail-value {
  font-size: 14px;
  color: #333;
  font-weight: 600;
}

.success-action-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 700 !important;
  letter-spacing: 0.5px !important;
}

.close-dialog-btn {
  text-transform: none !important;
  font-weight: 600 !important;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
